import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, SafeAreaView, Text, Image, RefreshControl, Alert, Button, Platform, Modal, TouchableOpacity, ScrollView } from 'react-native';
import { FAB, Card, Avatar, useTheme, ActivityIndicator, IconButton, Title } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { getMyChildren, deleteChild } from '../../api/childrenAPI';
import ChildCard from '../../components/ChildCard';
import axiosInstance from '../../api/axiosConfig';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { savePaginationState, loadPaginationState } from '../../utils/paginationUtils';

const SCREEN_KEY = 'user_children_screen';

const ChildrenScreen = ({ navigation }) => {
  const theme = useTheme();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  
  // Pagination state'ini localStorage'dan yükle
  const savedPagination = Platform.OS === 'web' ? 
    loadPaginationState(SCREEN_KEY, 1, 5) : 
    { currentPage: 1, itemsPerPage: 5 };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(savedPagination.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(savedPagination.itemsPerPage);
  
  // Pagination state değiştiğinde localStorage'a kaydet (web için)
  useEffect(() => {
    if (Platform.OS === 'web') {
      savePaginationState(SCREEN_KEY, currentPage, itemsPerPage);
    }
  }, [currentPage, itemsPerPage]);
  
  // Yaş hesaplama fonksiyonu
  const calculateAge = (birthdate) => {
    if (!birthdate) return 0;
    
    try {
      const today = new Date();
      
      // Tarih formatını düzenle ve doğru parse et
      let birthDate;
      
      if (typeof birthdate === 'string') {
        // API'den "YYYY-MM-DD" formatında geliyorsa (ISO formatı)
        // Saat dilimi sorunlarını önlemek için düzeltelim
        const parts = birthdate.split('T')[0].split('-');
        if (parts.length === 3) {
          birthDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else {
          birthDate = new Date(birthdate);
        }
      } else {
        birthDate = new Date(birthdate);
      }
      
      // Debug logları
      //console.log('Doğum tarihi (orijinal):', birthdate);
      //console.log('Doğum tarihi (parse edilmiş):', birthDate);
      //console.log('Bugün:', today);
      
      // Invalid date kontrolü
      if (isNaN(birthDate.getTime())) {
        console.error('Geçersiz tarih formatı:', birthdate);
        return 0;
      }
      
      // Doğum tarihi gelecekte mi kontrolü
      if (birthDate > today) {
        console.warn('Doğum tarihi gelecekte! Bugünden sonra:', birthdate);
        return 0;
      }
      
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      //console.log('Hesaplanan yaş:', age);
      return age;
    } catch (error) {
      console.error('Yaş hesaplama hatası:', error);
      return 0;
    }
  };
  
  // Yaşı veya gün/ay olarak süreyi formatla
  const formatAge = (birthdate) => {
    if (!birthdate) return '1 yaşından küçük';
    
    try {
      const today = new Date();
      let birthDate;
      
      // Tarih formatını parse et
      if (typeof birthdate === 'string') {
        const parts = birthdate.split('T')[0].split('-');
        if (parts.length === 3) {
          birthDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else {
          birthDate = new Date(birthdate);
        }
      } else {
        birthDate = new Date(birthdate);
      }
      
      // Geçersiz tarih kontrolü
      if (isNaN(birthDate.getTime())) {
        return '1 yaşından küçük';
      }
      
      // Tam yaş hesapla
      let years = today.getFullYear() - birthDate.getFullYear();
      let months = today.getMonth() - birthDate.getMonth();
      let days = today.getDate() - birthDate.getDate();
      
      // Ayları düzelt
      if (days < 0) {
        months--;
        // Önceki ayın gün sayısını ekle
        const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        days += lastMonth.getDate();
      }
      
      // Yılları düzelt
      if (months < 0) {
        years--;
        months += 12;
      }
      
      //console.log(`Detaylı yaş: ${years} yıl, ${months} ay, ${days} gün`);
      
      // Formatlanmış yaş ifadesi
      if (years >= 1) {
        return `${years} yaşında`;
      } else if (months >= 1) {
        return `${months} aylık`;
      } else {
        return `${days} günlük`;
      }
    } catch (error) {
      console.error('Yaş formatı hatası:', error);
      return '1 yaşından küçük';
    }
  };
  
  // Çocukları API'den getir
  const fetchChildren = async () => {
    try {
      setError(null);
      const response = await getMyChildren();
      //console.log('API yanıtı:', response);
      
      if (response.data) {
        //console.log('Çocuk verileri:', response.data);
        const formattedChildren = response.data.map(child => {
          // Her çocuk için //console.log ile ID'yi görelim
          //console.log(`Çocuk ID: ${child.id}, Tip: ${typeof child.id}`);
          
          return {
            id: child.id, // String'e dönüştürmeden orijinal formatını koruyalım
            name: child.first_name,
            surname: child.last_name,
            fullName: `${child.first_name} ${child.last_name}`,
            age: calculateAge(child.birthdate),
            ageDisplay: formatAge(child.birthdate),
            dateOfBirth: child.birthdate,
            image: null,
            activitiesCount: child.activities?.length || 0,
            gender: child.gender,
            hasSpecialNeeds: child.special_needs,
            allergies: child.allergies ? true : false
          };
        });
        
        setChildren(formattedChildren);
      }
    } catch (err) {
      console.error('Error fetching children:', err);
      setError('Çocuk bilgilerini getirirken bir hata oluştu.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Sayfa odaklandığında çalışacak
  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      fetchChildren();
      
      return () => {
        // Temizleme işlemleri (gerekirse)
      };
    }, [])
  );
  
  // Yenileme işlemi
  const onRefresh = () => {
    setRefreshing(true);
    fetchChildren();
    setCurrentPage(1); // Yenileme sırasında ilk sayfaya dön
  };
  
  // Çocuk silme işlemi
  const handleDeleteChild = (child) => {
    //console.log('Silmeye çalışılan çocuk:', child);
    //console.log('Çocuk ID:', child.id);
    
    // Çocuğu seç ve modalı göster
    setSelectedChild(child);
    setDeleteModalVisible(true);
  };
  
  // Silme işlemini onayla
  const confirmDelete = async () => {
    try {
      if (!selectedChild) return;
      
      //console.log('Silme API çağrısı yapılıyor...');
      //console.log('deleteChild fonksiyonu çağrılıyor. childId:', selectedChild.id);
      
      const response = await deleteChild(selectedChild.id);
      //console.log('Silme yanıtı:', response);
      
      // Modalı kapat
      setDeleteModalVisible(false);
      
      // Başarı bildirimi göster
      Alert.alert('Başarılı', 'Çocuk profili başarıyla silindi.');
      
      // Listeyi güncelle
      fetchChildren();
    } catch (err) {
      console.error('Error deleting child:', err);
      
      // Modalı kapat
      setDeleteModalVisible(false);
      
      // Hata bildirimi göster
      Alert.alert('Hata', err.message || 'Çocuk profili silinirken bir hata oluştu.');
    }
  };
  
  // Silme işlemini iptal et
  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setSelectedChild(null);
  };
  
  // Düzenleme sayfasına yönlendirme
  const handleEditChild = (child) => {
    navigation.navigate('AddChild', { childId: child.id });
  };

  // Çocuk detay sayfasına yönlendirme
  const navigateToChildDetail = (child) => {
    navigation.navigate('ChildDetail', { childId: child.id });
  };
  
  const renderChildItem = ({ item }) => {
    //console.log('Render edilen çocuk item:', item);
    return (
      <ChildCard
        child={item}
        onPress={() => navigateToChildDetail(item)}
        //onEdit={() => handleEditChild(item)}
        //onDelete={() => handleDeleteChild(item)}
        showOptions={true}
      />
    );
  };
  
  // Pagination hesaplamaları
  const totalPages = Math.ceil(children.length / itemsPerPage);
  
  // Mevcut sayfada gösterilecek çocuklar
  const paginatedChildren = children.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );
  
  // Önceki sayfaya geç
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Sonraki sayfaya geç
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Sayfa numaralarını göster
  const renderPagination = () => {
    // Tüm durumlarda pagination'ı göster
    return (
      <View style={styles.paginationContainer}>
        <IconButton
          icon="chevron-left"
          size={24}
          onPress={goToPreviousPage}
          disabled={currentPage === 1}
          style={[
            styles.paginationButton,
            currentPage === 1 ? styles.paginationButtonDisabled : null
          ]}
          color={currentPage === 1 ? '#BDBDBD' : theme.colors.primary}
        />
        
        <Text style={styles.paginationText}>
          Sayfa {currentPage} / {Math.max(totalPages, 1)} ({children.length} kayıt)
        </Text>
        
        <IconButton
          icon="chevron-right"
          size={24}
          onPress={goToNextPage}
          disabled={currentPage >= totalPages}
          style={[
            styles.paginationButton,
            currentPage >= totalPages ? styles.paginationButtonDisabled : null
          ]}
          color={currentPage >= totalPages ? '#BDBDBD' : theme.colors.primary}
        />
      </View>
    );
  };
  
  // Yükleniyor durumu
  if (loading && !refreshing) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  // Hata durumu
  if (error) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>{error}</Text>
        <Button
          mode="contained"
          onPress={fetchChildren}
          style={{ marginTop: 20 }}
        >
          Yeniden Dene
        </Button>
      </View>
    );
  }
  
  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Title style={styles.screenTitle}>Çocuklarım</Title>
        <Text style={styles.subtitle}>Çocuklarınızın profillerini ekleyin ve yönetin</Text>
      </ScrollView>
      
      {children.length > 0 ? (
        <>
          <FlatList
            data={paginatedChildren}
            renderItem={renderChildItem}
            keyExtractor={item => item.id}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl 
                refreshing={refreshing} 
                onRefresh={onRefresh} 
                colors={[theme.colors.primary]}
              />
            }
          />
          {renderPagination()}
        </>
      ) : (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="account-child" size={80} color="rgb(125, 76, 236)" />
          <Text style={styles.emptyTitle}>Henüz çocuk eklenmedi</Text>
          <Text style={styles.emptyMessage}>
            Çocuk ekleyerek aktivitelerini yönetmeye başlayabilirsiniz
          </Text>
        </View>
      )}
      
      <FAB
        icon="plus"
        style={styles.fab}
        color="white"
        onPress={() => navigation.navigate('AddChild')}
      />
      
      {/* Silme Onay Modalı */}
      <Modal
        visible={deleteModalVisible}
        transparent={true}
        onRequestClose={cancelDelete}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Çocuk Profilini Sil</Text>
            </View>
            
            <View style={styles.modalBody}>
              <MaterialCommunityIcons 
                name="alert-circle-outline" 
                size={40} 
                color="#E53935" 
                style={styles.warningIcon}
              />
              <Text style={styles.modalText}>
                {selectedChild ? `${selectedChild.fullName || selectedChild.name + ' ' + selectedChild.surname} profilini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.` : ''}
              </Text>
            </View>
            
            <View style={styles.modalFooter}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.cancelButton]}
                onPress={cancelDelete}
              >
                <Text style={styles.buttonText}>Vazgeç</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalButton, styles.deleteButton]}
                onPress={confirmDelete}
              >
                <Text style={[styles.buttonText, styles.deleteButtonText]}>Sil</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',  
  },
  // Modal Stilleri
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '100%',
    maxWidth: 400,
    elevation: 5,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
  },
  modalHeader: {
    backgroundColor: 'rgba(125, 76, 236, 0.1)',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(125, 76, 236, 0.3)',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  modalBody: {
    padding: 20,
    alignItems: 'center',
  },
  warningIcon: {
    marginBottom: 16,
  },
  modalText: {
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
    textAlign: 'center',
    lineHeight: 22,
  },
  modalFooter: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: 'rgba(125, 76, 236, 0.3)',
  },
  modalButton: {
    flex: 1,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    borderRightWidth: 1,
    borderRightColor: 'rgba(125, 76, 236, 0.3)',
  },
  deleteButton: {
    backgroundColor: 'rgba(125, 76, 236, 0.1)',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '500',
    color: 'rgb(125, 76, 236)',
  },
  deleteButtonText: {
    color: 'rgb(210, 50, 80)',
  },
  scrollContent: {
    padding: 16,
    paddingTop: 24,
  },
  screenTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
    color: 'rgb(125, 76, 236)',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderRadius: 8,
    padding: 8,
  },
  subtitle: {
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
    marginTop: 4,
    marginBottom: 16,
    textAlign: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: 8,
    padding: 6,
  },
  listContent: {
    padding: 16,
    
  
  },
  childCard: {
    marginBottom: 16,
    padding: 16,
    elevation: 2,
    backgroundColor: 'rgb(232, 162, 249)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(5, 3, 3, 0.3)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  childInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  childDetails: {
    marginLeft: 16,
    flex: 1,
  },
  childName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  childAge: {
    fontSize: 14,
    color: 'rgb(90, 50, 180)',
    marginTop: 2,
  },
  activityInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  activityText: {
    fontSize: 14,
    color: 'rgb(90, 50, 180)',
    marginLeft: 4,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 16,
    textAlign: 'center',
    color: 'rgb(125, 76, 236)',
  },
  emptyMessage: {
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
    textAlign: 'center',
    marginTop: 8,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
  },
  errorText: {
    marginTop: 16,
    fontSize: 16,
    color: 'rgb(210, 50, 80)',
    textAlign: 'center',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#eeeeee',
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  paginationButton: {
    margin: 0,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 14,
    marginHorizontal: 12,
    color: 'rgb(125, 76, 236)',
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgb(125, 76, 236)',
  },
});

export default ChildrenScreen;
